class BundleBuilder extends HTMLElement {
  constructor() {
    super();

    this.onClick = this.onClick.bind(this);
    this.onPlanChange = this.onPlanChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onBannerScroll = this.onBannerScroll.bind(this);
  }

  connectedCallback() {
    this.maxTotal = Number(this.dataset.maxTotal) || 6;
    this.packQuantity = Number(this.dataset.packQuantity) || this.maxTotal;
    this.samePrice = this.dataset.samePrice === 'true';
    this.quantities = new Map();
    this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
    this.slots = Array.from(this.querySelectorAll('[data-bundle-slot]'));
    this.counter = this.querySelector('[data-bundle-counter]');
    this.ctaButton = this.querySelector('[data-bundle-cta]');
    this.planInputs = Array.from(this.querySelectorAll('[data-bundle-plan]'));
    this.plansPanel = this.querySelector('[data-bundle-plans]');
    this.banner = this.closest('.bundle-builder-section')?.querySelector('[data-bundle-banner]');
    this.bannerScrollLink = this.banner?.querySelector('[data-bundle-scroll]');
    this.bundleConfig = this.getBundleCartConfig();

    this.cards.forEach((card) => {
      this.quantities.set(card.dataset.bundleCard, 0);
    });

    this.addEventListener('click', this.onClick);
    this.planInputs.forEach((input) => input.addEventListener('change', this.onPlanChange));
    this.ctaButton?.addEventListener('click', this.onSubmit);
    this.bannerScrollLink?.addEventListener('click', this.onBannerScroll);

    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.planInputs.forEach((input) => input.removeEventListener('change', this.onPlanChange));
    this.ctaButton?.removeEventListener('click', this.onSubmit);
    this.bannerScrollLink?.removeEventListener('click', this.onBannerScroll);
  }

  onBannerScroll(event) {
    const target = document.getElementById(this.bannerScrollLink.hash.slice(1));
    if (!target) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  getBundleCartConfig() {
    const configElement = this.querySelector('[data-bundle-cart-config]');

    if (!configElement) {
      return {
        bundleVariantId: Number(this.dataset.bundleProductVariantId) || null,
        sellingPlanId: this.dataset.sellingPlanId ? Number(this.dataset.sellingPlanId) : null,
        discountPercent: Number(this.dataset.bundleDiscountPercent) || 0,
        packQuantity: this.packQuantity,
        bundleName: this.dataset.bundleName || '',
      };
    }

    try {
      const config = JSON.parse(configElement.textContent);

      return {
        bundleVariantId: Number(config.bundleVariantId) || null,
        sellingPlanId: config.sellingPlanId ? Number(config.sellingPlanId) : null,
        discountPercent: Number(config.discountPercent) || 0,
        packQuantity: Number(config.packQuantity) || this.packQuantity,
        bundleName: config.bundleName || '',
      };
    } catch (error) {
      return {
        bundleVariantId: Number(this.dataset.bundleProductVariantId) || null,
        sellingPlanId: this.dataset.sellingPlanId ? Number(this.dataset.sellingPlanId) : null,
        discountPercent: Number(this.dataset.bundleDiscountPercent) || 0,
        packQuantity: this.packQuantity,
        bundleName: this.dataset.bundleName || '',
      };
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  getCardByVariantId(variantId) {
    return this.cards.find((card) => Number(card.dataset.variantId) === variantId);
  }

  getBundleDisplayName() {
    if (this.bundleConfig.bundleName) {
      return this.bundleConfig.bundleName;
    }

    for (const slot of this.slots) {
      const variantId = Number(slot.dataset.variantId);
      if (!variantId) continue;

      const card = this.getCardByVariantId(variantId);
      const bundleName = card?.dataset.bundleName;

      if (bundleName) {
        return bundleName;
      }
    }

    return '';
  }

  onClick(event) {
    const selectButton = event.target.closest('[data-bundle-select]');
    const minusButton = event.target.closest('[data-bundle-quantity-minus]');
    const plusButton = event.target.closest('[data-bundle-quantity-plus]');

    if (selectButton && this.contains(selectButton)) {
      const card = selectButton.closest('[data-bundle-card]');
      if (!card || this.getTotalCount() >= this.maxTotal) return;

      const blockId = card.dataset.bundleCard;
      this.setCardQuantity(card, (this.quantities.get(blockId) || 0) + 1);
      return;
    }

    if (minusButton && this.contains(minusButton)) {
      const card = minusButton.closest('[data-bundle-card]');
      if (!card) return;

      const blockId = card.dataset.bundleCard;
      this.setCardQuantity(card, (this.quantities.get(blockId) || 0) - 1);
      return;
    }

    if (plusButton && this.contains(plusButton)) {
      const card = plusButton.closest('[data-bundle-card]');
      if (!card) return;

      const blockId = card.dataset.bundleCard;
      this.setCardQuantity(card, (this.quantities.get(blockId) || 0) + 1);
      return;
    }

    const removeButton = event.target.closest('[data-bundle-slot-remove]');
    if (removeButton && this.contains(removeButton)) {
      const slot = removeButton.closest('[data-bundle-slot]');
      const blockId = slot?.dataset.bundleSlotBlockId;
      if (!blockId) return;

      const card = this.cards.find((item) => item.dataset.bundleCard === blockId);
      if (!card) return;

      this.setCardQuantity(card, (this.quantities.get(blockId) || 0) - 1);
    }
  }

  onPlanChange() {
    this.updateCta();
  }

  setCardQuantity(card, requestedValue) {
    const blockId = card.dataset.bundleCard;
    const currentQty = this.quantities.get(blockId) || 0;
    const available = this.maxTotal - (this.getTotalCount() - currentQty);
    const nextValue = Math.max(0, Math.min(requestedValue, available));

    this.quantities.set(blockId, nextValue);

    const quantityWrap = card.querySelector('[data-bundle-quantity]');
    const selectButton = card.querySelector('[data-bundle-select]');
    const input = quantityWrap?.querySelector('[data-bundle-quantity-input]');
    const minusButton = quantityWrap?.querySelector('[data-bundle-quantity-minus]');
    const plusButton = quantityWrap?.querySelector('[data-bundle-quantity-plus]');

    if (input) {
      input.value = nextValue;
      input.max = available;
    }

    if (minusButton) {
      minusButton.disabled = nextValue <= 0;
    }

    if (plusButton) {
      plusButton.disabled = nextValue >= available;
    }

    if (nextValue > 0) {
      selectButton?.setAttribute('hidden', '');
      quantityWrap?.removeAttribute('hidden');
    } else {
      selectButton?.removeAttribute('hidden');
      quantityWrap?.setAttribute('hidden', '');
    }

    this.render();
  }

  getTotalCount() {
    let total = 0;

    this.quantities.forEach((qty) => {
      total += qty;
    });

    return total;
  }

  buildSlotItems() {
    const items = [];

    this.cards.forEach((card) => {
      const blockId = card.dataset.bundleCard;
      const qty = this.quantities.get(blockId) || 0;

      for (let index = 0; index < qty; index += 1) {
        items.push({
          blockId,
          variantId: Number(card.dataset.variantId),
          variantPrice: Number(card.dataset.variantPrice) || 0,
          title: card.dataset.productTitle,
          image: card.dataset.productImage,
        });
      }
    });

    return items;
  }

  getSlotOnetimeTotal() {
    let total = 0;

    this.slots.forEach((slot) => {
      const variantId = Number(slot.dataset.variantId);
      if (!variantId) return;

      const card = this.getCardByVariantId(variantId);
      total += Number(card?.dataset.variantPrice) || 0;
    });

    return total;
  }

  getSlotSubscribeTotal() {
    let total = 0;
    let selectedCount = 0;
    let exactPriceCount = 0;

    this.slots.forEach((slot) => {
      const variantId = Number(slot.dataset.variantId);
      if (!variantId) return;

      selectedCount += 1;

      const card = this.getCardByVariantId(variantId);
      const sellingPlanPrice = Number(card?.dataset.sellingPlanPrice) || 0;

      if (sellingPlanPrice > 0) {
        exactPriceCount += 1;
      }

      total += sellingPlanPrice;
    });

    return selectedCount > 0 && exactPriceCount === selectedCount ? total : null;
  }

  getSamePriceTotal(priceKey) {
    return (Number(this.cards[0]?.dataset[priceKey]) || 0) * this.maxTotal;
  }

  updatePlanPricing() {
    const isComplete = this.getTotalCount() === this.maxTotal;
    const useSamePrice = this.samePrice && !isComplete;
    const showPrices = isComplete || this.samePrice;
    const onetimeTotal = useSamePrice ? this.getSamePriceTotal('variantPrice') : this.getSlotOnetimeTotal();
    const discountPercent = this.bundleConfig.discountPercent || 0;
    const packQty = this.bundleConfig.packQuantity || this.maxTotal;
    const exactSubscribeTotal = useSamePrice
      ? this.getSamePriceTotal('sellingPlanPrice') || null
      : this.getSlotSubscribeTotal();
    const subscribeDiscount = Math.round((onetimeTotal * discountPercent) / 100);
    const subscribeTotal = exactSubscribeTotal ?? onetimeTotal - subscribeDiscount;
    const onetimeUnit = packQty > 0 ? Math.round(onetimeTotal / packQty) : 0;
    const subscribeUnit = packQty > 0 ? Math.round(subscribeTotal / packQty) : 0;

    const onetimeTotalEl = this.querySelector('[data-bundle-plan-onetime-total]');
    const onetimeUnitEl = this.querySelector('[data-bundle-plan-onetime-unit]');
    const subscribeCompareEl = this.querySelector('[data-bundle-plan-subscribe-compare]');
    const subscribeTotalEl = this.querySelector('[data-bundle-plan-subscribe-total]');
    const subscribeUnitEl = this.querySelector('[data-bundle-plan-subscribe-unit]');

    const togglePrice = (element, cents, suffix = '') => {
      if (!element) return;

      if (!showPrices || cents <= 0) {
        element.hidden = true;
        element.textContent = '';
        return;
      }

      element.hidden = false;
      element.textContent = `${this.formatMoney(cents)}${suffix}`;
    };

    togglePrice(onetimeTotalEl, onetimeTotal);
    togglePrice(onetimeUnitEl, onetimeUnit, '/box');
    togglePrice(subscribeCompareEl, onetimeTotal);
    togglePrice(subscribeTotalEl, subscribeTotal);
    togglePrice(subscribeUnitEl, subscribeUnit, '/box');
  }

  render() {
    const total = this.getTotalCount();
    const slotItems = this.buildSlotItems();

    if (this.counter) {
      this.counter.textContent = `(${total}/${this.maxTotal})`;
    }

    this.slots.forEach((slot, index) => {
      const item = slotItems[index];
      const image = slot.querySelector('[data-bundle-slot-image]');
      const removeButton = slot.querySelector('[data-bundle-slot-remove]');

      if (item?.variantId && image) {
        if (item.image) {
          image.src = item.image;
        }
        image.alt = item.title || '';
        slot.dataset.bundleSlotBlockId = item.blockId;
        slot.dataset.variantId = item.variantId;
        slot.classList.add('is-filled');
        removeButton?.removeAttribute('hidden');
      } else {
        if (image) {
          image.removeAttribute('src');
          image.alt = '';
        }
        delete slot.dataset.bundleSlotBlockId;
        delete slot.dataset.variantId;
        slot.classList.remove('is-filled');
        removeButton?.setAttribute('hidden', '');
      }
    });

    this.cards.forEach((card) => {
      const blockId = card.dataset.bundleCard;
      const qty = this.quantities.get(blockId) || 0;
      const available = this.maxTotal - (this.getTotalCount() - qty);
      const selectButton = card.querySelector('[data-bundle-select]');
      const plusButton = card.querySelector('[data-bundle-quantity-plus]');
      const minusButton = card.querySelector('[data-bundle-quantity-minus]');
      const input = card.querySelector('[data-bundle-quantity-input]');

      if (selectButton) {
        selectButton.disabled = this.getTotalCount() >= this.maxTotal;
      }

      if (input) {
        input.max = available;
      }

      if (plusButton) {
        plusButton.disabled = qty >= available;
      }

      if (minusButton) {
        minusButton.disabled = qty <= 0;
      }
    });

    this.updatePlanPricing();
    this.updateCta();
    this.updateBanner();
  }

  updateBanner() {
    if (!this.banner) return;

    const isComplete = this.getTotalCount() === this.maxTotal;
    this.banner.classList.toggle('is-complete', isComplete);

    const header = document.querySelector('.header__wrapper--transparent-bundle');
    header?.classList.toggle('is-bundle-complete', isComplete);
  }

  updateCta() {
    if (!this.ctaButton) return;

    const total = this.getTotalCount();
    const isComplete = total === this.maxTotal;
    const hasBundleProduct = Boolean(this.bundleConfig.bundleVariantId);
    const incompleteLabel = this.dataset.ctaIncomplete || 'Add 6 packs to continue';
    const completeLabel = this.dataset.ctaComplete || 'Add to cart';

    this.ctaButton.textContent = isComplete ? completeLabel : incompleteLabel;
    this.ctaButton.disabled = !isComplete || !hasBundleProduct;
    this.ctaButton.classList.toggle('bundle-builder__cta--complete', isComplete && hasBundleProduct);
    this.ctaButton.classList.toggle('bundle-builder__cta--incomplete', !isComplete || !hasBundleProduct);
  }

  createBundleId() {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = Math.floor(Math.random() * 16);
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  getSlotVariantQuantities() {
    const quantities = new Map();

    this.slots.forEach((slot) => {
      const variantId = Number(slot.dataset.variantId);

      if (!variantId) return;

      quantities.set(variantId, (quantities.get(variantId) || 0) + 1);
    });

    return quantities;
  }

  buildCartItems() {
    const slotVariants = this.getSlotVariantQuantities();
    const items = [];
    const bundleVariantId = this.bundleConfig.bundleVariantId;

    if (slotVariants.size === 0) return items;

    if (!bundleVariantId) {
      return items;
    }

    const plan = this.querySelector('[data-bundle-plan]:checked')?.value;
    const sellingPlanId = plan === 'subscribe' ? this.bundleConfig.sellingPlanId : null;
    const bundleId = this.createBundleId();

    slotVariants.forEach((quantity, variantId) => {
      const componentItem = {
        id: variantId,
        quantity,
        properties: {
          _bundle_id: bundleId,
        },
      };

      if (sellingPlanId) {
        componentItem.selling_plan = sellingPlanId;
      }

      items.push(componentItem);
    });

    const parentItem = {
      id: bundleVariantId,
      quantity: 1,
      properties: {
        _bundle_id: bundleId,
        _bundle_parent: true,
      },
    };

    const bundleName = this.getBundleDisplayName();
    if (bundleName) {
      parentItem.properties._bundle_name = bundleName;
    }

    if (sellingPlanId) {
      parentItem.selling_plan = sellingPlanId;
    }

    items.push(parentItem);

    return items;
  }

  async onSubmit() {
    if (!this.ctaButton || this.ctaButton.disabled) return;

    const items = this.buildCartItems();

    if (items.length === 0) {
      if (!this.bundleConfig.bundleVariantId) {
        window.alert('Bundle product is not configured. Please select it in theme settings.');
      }
      return;
    }

    this.ctaButton.setAttribute('aria-disabled', 'true');
    this.ctaButton.classList.add('is-loading');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'Unable to add bundle to cart.');
      }

      document.dispatchEvent(
        new CustomEvent('product-form:added', {
          bubbles: true,
          detail: {
            cartItem: parsedState,
          },
        })
      );
    } catch (error) {
      window.alert(error.message);
    } finally {
      this.ctaButton.removeAttribute('aria-disabled');
      this.ctaButton.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
